import { get, post, del } from "@crunchit/http-client";
import { IAvailableDate, IAvailableTime, ICheckout, ICheckoutDiscount, ICheckoutGuestInformation, ICheckoutInstanceSettings, IPickupTimesRequestBody } from "@crunchit/types";
import { getToken, getEnvironment } from "utils/helpers/service";

const api = process.env.REACT_APP_CHECKOUT_URI || `checkout.${getEnvironment()}crunchy.ninja/api`;

const CheckoutService = {
  getCheckoutById: async (id: string) => {
    return get<ICheckout>(`${api}/checkout/${id}`, getToken());
  },
  getAvailablePickupDates: async (checkoutInstanceId: string, productionUnitId: number) => {
    return get<IAvailableDate[]>(`${api}/availableordertime/${checkoutInstanceId}/${productionUnitId}`, getToken());
  },
  getAvailableDeliveryDates: async (checkoutId: string) => {
    return get<IAvailableDate[]>(`${api}/checkout/${checkoutId}/delivery/dates`, getToken());
  },
  getAvailableDeliveryTimes: async (checkoutId: string) => {
    return get<IAvailableTime[]>(`${api}/checkout/${checkoutId}/delivery/times`, getToken());
  },
  getCheckoutInstanceSettings: async (checkoutInstanceId: string) => {
    return get<ICheckoutInstanceSettings>(`${api}/instancesetting/${checkoutInstanceId}`, getToken());
  },

  createCheckout: async (checkoutInstanceId: string, moduleId: string) => {
    return post<ICheckout>(`${api}/checkout?instanceId=${checkoutInstanceId}&moduleId=${moduleId}`, {}, getToken());
  },
  addCheckoutDeliveryAddress: async (checkoutId: string, moduleId: string, address: string, zipCode: string) => {
    return post<ICheckout>(`${api}/checkout/${checkoutId}/delivery/address?moduleId=${moduleId}&address=${address}&zipcode=${zipCode}`, {}, getToken());
  },
  addCheckoutDeliveryDate: async (checkoutId: string, dateTimeString: string) => {
    return post<undefined>(`${api}/checkout/${checkoutId}/delivery/date`, dateTimeString, getToken());
  },
  addCheckoutDeliveryTime: async (checkoutId: string, dateTimeString: string) => {
    return post<undefined>(`${api}/checkout/${checkoutId}/delivery/time`, dateTimeString, getToken());
  },
  addCheckoutBasket: async (checkoutId: string, basketId: string) => {
    return post<undefined>(`${api}/checkout/${checkoutId}/basket?basketId=${basketId}`, {}, getToken());
  },
  addCheckoutGuestInformation: async (checkoutId: string, guestInformation: ICheckoutGuestInformation) => {
    return post<ICheckout>(`${api}/checkout/${checkoutId}/guestInformation`, guestInformation, getToken());
  },
  addCheckoutDiscount: async (checkoutId: string, discount: Partial<ICheckoutDiscount>) => {
    return post<ICheckout>(`${api}/checkout/${checkoutId}/discount`, discount, getToken());
  },
  addCheckoutGiftcard: async (checkoutId: string, giftcardCode: string, amount: number) => {
    return post<ICheckout>(`${api}/checkout/${checkoutId}/giftcard`, { giftcardCode, amount }, getToken());
  },
  addCheckoutPayment: async (checkoutId: string, moduleId: string, continueUrl: string, cancelUrl: string, paymentMethods?: string) => {
    let params = `?moduleId=${moduleId}&continuationUrl=${continueUrl}&cancellationUrl=${cancelUrl}`;

    if (paymentMethods) {
      params += `&paymentMethods=${paymentMethods}`;
    }

    return post<any>(`${api}/checkout/${checkoutId}/payment${params}`, {}, getToken());
  },
  updatePickupTimes: async (checkoutInstanceId: string, pickup: IPickupTimesRequestBody) => {
    return post<IAvailableTime[]>(`${api}/availableordertime/${checkoutInstanceId}`, pickup, getToken());
  },
  validateOrder: async (checkoutId: string, productionUnitId: number) => {
    return post<undefined>(`${api}/order/validate?checkoutId=${checkoutId}`, { productionUnitId }, getToken());
  },
  finalizeCheckout: async (checkoutId: string) => {
    return post<ICheckout>(`${api}/checkout/${checkoutId}/finalize`, {}, getToken());
  },

  removeDiscount: async (checkoutId: string, discountId: string) => {
    return del<ICheckout>(`${api}/checkout/${checkoutId}/discount/${discountId}`, getToken());
  },
  removeGiftcard: async (checkoutId: string, giftcardId: string) => {
    return del<ICheckout>(`${api}/checkout/${checkoutId}/giftcard/${giftcardId}`, getToken());
  },
  removeDeliveryInfo: async (checkoutId: string) => {
    return del<undefined>(`${api}/checkout/${checkoutId}/delivery`, getToken());
  },
};

export default CheckoutService;
